import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  api,
  getErrorMessage,
  type ContactDataPoint,
  type ContactPurchaseBody,
} from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import type { CollectionCompany } from "./entryStatus.ts";
import DataProviderLogo from "../../../components/DataProviderLogo.tsx";
import LogoBadge from "../../../components/LogoBadge.tsx";
import { formatMoney } from "../../../utils/format.ts";

const REVEALS: { key: ContactDataPoint; label: string }[] = [
  { key: "work_email", label: "Work email" },
  { key: "mobile_phone", label: "Phone number" },
];

interface FindContactsDialogProps<T extends CollectionCompany> {
  orgId: string;
  listId: string;
  prospect: T | null;
  onClose: () => void;
  onStarted: (prospect: T) => void;
}

function parseTitles(text: string): string[] {
  return text
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

/**
 * Buy people (and their work email / phone) at one company from a data
 * provider. Always quotes first: the estimate is the same calculation the
 * purchase reserves against the organization's monthly budget. A partner
 * provider (GetLeads, `billed: false`) is free: no prices, no budget.
 */
export default function FindContactsDialog<T extends CollectionCompany>({
  orgId,
  listId,
  prospect,
  onClose,
  onStarted,
}: FindContactsDialogProps<T>) {
  const queryClient = useQueryClient();
  const [limit, setLimit] = useState(5);
  const [reveals, setReveals] = useState<ContactDataPoint[]>(["work_email"]);
  const [titlesText, setTitlesText] = useState("");
  const [appliedTitles, setAppliedTitles] = useState<string[] | null>(null);
  // null: the server's default, the first provider enabled.
  const [provider, setProvider] = useState<string | null>(null);

  useEffect(() => {
    if (prospect) {
      setLimit(5);
      setReveals(["work_email"]);
      setTitlesText("");
      setAppliedTitles(null);
      setProvider(null);
    }
  }, [prospect]);

  const providers = useQuery({
    queryKey: keys.contactEnrichmentEstimate.providers(orgId),
    queryFn: () => api.listContactPurchaseProviders(orgId),
    enabled: !!prospect,
  });
  const options = providers.data ?? [];

  // A company the database does not hold is bought at by its list entry.
  const body: ContactPurchaseBody | null = prospect
    ? {
        collection_id: listId,
        ...(prospect.master_company_id
          ? { master_company_id: prospect.master_company_id }
          : { collection_entry_id: prospect.id }),
        limit,
        data_points: reveals,
        titles: appliedTitles,
        ...(provider ? { provider } : {}),
      }
    : null;

  const estimate = useQuery({
    queryKey: keys.contactEnrichmentEstimate.quote(orgId, body),
    queryFn: () => api.estimateContactPurchase(orgId, body!),
    enabled: !!body && limit >= 1,
    placeholderData: keepPreviousData,
  });
  const quote = estimate.data;

  // Untick what the provider is not set up to buy, so the default is buyable.
  const available = quote?.available_data_points;
  useEffect(() => {
    if (!available) return;
    setReveals((prev) => {
      const next = prev.filter((k) => available.includes(k));
      return next.length === prev.length ? prev : next;
    });
  }, [available]);

  const buy = useMutation({
    mutationFn: () => api.createContactPurchase(orgId, body!),
    onSuccess: (job) => {
      queryClient.invalidateQueries({ queryKey: keys.contactEnrichmentJob.all });
      toast.success(`Finding contacts at ${job.company_name ?? "the company"}…`);
      if (prospect) onStarted(prospect);
      onClose();
    },
  });

  const applyTitles = () => setAppliedTitles(parseTitles(titlesText));

  const toggleReveal = (key: ContactDataPoint, on: boolean) =>
    setReveals((prev) => (on ? [...prev, key] : prev.filter((k) => k !== key)));

  const currency = quote?.currency ?? "USD";
  // Free until the quote says otherwise only if the chosen provider is free.
  const chosen = options.find((o) => o.provider === (provider ?? quote?.provider)) ?? options[0];
  const free = quote ? !quote.billed : chosen ? !chosen.billed : false;
  const maxLimit = quote?.max_limit ?? 25;
  const titlesDirty = JSON.stringify(parseTitles(titlesText)) !== JSON.stringify(appliedTitles ?? []);

  return (
    <Dialog open={!!prospect} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Find contacts at {prospect?.company_name ?? "this company"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {free ? "Finds" : "Buys"} people currently working at{" "}
            {quote?.company_domain ?? prospect?.company_domain ?? "the company"} from{" "}
            {quote?.provider_name ?? "the data provider"} and adds them to this list.{" "}
            {free
              ? "Free: this provider is a partner, so nothing is billed."
              : "Billed to your organization; you only pay for what is found, and data we already hold is never bought again."}
          </Typography>

          {options.length > 1 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Provider
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={provider ?? quote?.provider ?? options[0].provider}
                onChange={(_, value: string | null) => value && setProvider(value)}
              >
                {options.map((option) => {
                  const profile = option.prices.person_profile;
                  return (
                    <ToggleButton
                      key={option.provider}
                      value={option.provider}
                      sx={{ justifyContent: "flex-start", gap: 1.25, textTransform: "none", py: 1 }}
                    >
                      <LogoBadge size={28}>
                        <DataProviderLogo name={option.provider_name} size={28} />
                      </LogoBadge>
                      <Box sx={{ textAlign: "left" }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {option.provider_name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {!option.billed
                            ? "Free"
                            : profile != null
                              ? `${formatMoney(profile, option.currency)} per person`
                              : "Not priced yet"}
                        </Typography>
                      </Box>
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
            </Box>
          )}

          {quote && quote.contacts_on_list > 0 && (
            <Alert severity="info">
              {quote.contacts_on_list} contact{quote.contacts_on_list === 1 ? " is" : "s are"}{" "}
              already in this collection for this company. They are skipped
              {free ? "." : " and not charged."}
            </Alert>
          )}

          <Stack direction="row" spacing={2}>
            <TextField
              label="People"
              type="number"
              value={limit}
              onChange={(e) => setLimit(Math.max(1, Math.min(maxLimit, Number(e.target.value) || 1)))}
              slotProps={{ htmlInput: { min: 1, max: maxLimit } }}
              sx={{ width: 120 }}
              helperText={`Max ${maxLimit}`}
            />
            <TextField
              label="Job titles"
              value={titlesText}
              onChange={(e) => setTitlesText(e.target.value)}
              onBlur={applyTitles}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyTitles();
              }}
              fullWidth
              placeholder="e.g. CTO, Head of IT"
              helperText={
                titlesDirty
                  ? "Press Enter to update the quote"
                  : "Comma-separated; empty means anyone."
              }
            />
          </Stack>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Also {free ? "get" : "buy"} for each person
            </Typography>
            <FormGroup row>
              {REVEALS.map(({ key, label }) => {
                const available = !quote || quote.available_data_points.includes(key);
                return (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={reveals.includes(key)}
                        onChange={(e) => toggleReveal(key, e.target.checked)}
                        disabled={!available}
                      />
                    }
                    label={
                      <>
                        {label}
                        {!free && quote?.prices[key] != null && (
                          <Typography component="span" variant="body2" sx={{ color: "text.secondary" }}>
                            {" "}
                            · {formatMoney(quote.prices[key]!, currency)}
                          </Typography>
                        )}
                        {!available && (
                          <Typography component="span" variant="body2" sx={{ color: "text.secondary" }}>
                            {" "}
                            (not available)
                          </Typography>
                        )}
                      </>
                    }
                  />
                );
              })}
            </FormGroup>
          </Box>

          <Divider />

          {estimate.isLoading ? (
            <Stack spacing={0.5}>
              <Skeleton variant="text" width="55%" />
              <Skeleton variant="text" width="35%" height={28} />
            </Stack>
          ) : estimate.isError ? (
            <Alert severity="error">{getErrorMessage(estimate.error)}</Alert>
          ) : quote && free ? (
            <Stack spacing={1}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Free
              </Typography>
              {quote.blocked_reason && <Alert severity="warning">{quote.blocked_reason}</Alert>}
            </Stack>
          ) : quote ? (
            <Stack spacing={1}>
              {quote.prices.person_profile != null && (
                <Typography variant="body2">
                  Person profile (name, title, LinkedIn):{" "}
                  {formatMoney(quote.prices.person_profile, currency)} each
                </Typography>
              )}
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Up to {formatMoney(quote.max_cost, currency)}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Monthly budget left:{" "}
                {quote.budget.organization
                  ? formatMoney(quote.budget.remaining, currency)
                  : "no budget set"}
                {quote.budget.tenant && " (client limit applies)"}
              </Typography>
              {quote.blocked_reason && <Alert severity="warning">{quote.blocked_reason}</Alert>}
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => buy.mutate()}
          loading={buy.isPending}
          disabled={!quote?.can_purchase || estimate.isFetching || titlesDirty}
        >
          {!quote
            ? "Buy"
            : free
              ? `Find up to ${limit}`
              : `Buy up to ${limit} (max ${formatMoney(quote.max_cost, currency)})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
